'use client';

import { useCallback, useEffect, useState } from 'react';

import { fetchWithAppAuth } from '@/lib/auth/client-api';
import type {
  AccountSavedItemDoc,
  AccountSavedItemType,
  AccountStats,
  LicenseDoc,
  NotificationDoc,
  OrderDoc,
  SubscriptionDoc,
} from '@/types/platform';

type AccountListResponse<T> = {
  ok?: boolean;
  items?: T[];
  nextCursor?: string | null;
  limit?: number;
  error?: string;
};

type AccountSummary = {
  stats: AccountStats;
  subscription: SubscriptionDoc | null;
};

type AccountSummaryResponse = Partial<AccountSummary> & {
  ok?: boolean;
  error?: string;
};

type SaveAccountItemInput = {
  itemType: AccountSavedItemType;
  itemId: string;
  title?: string;
  href?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
};

type DeleteAccountSavedItemInput = {
  savedId?: string;
  itemType?: AccountSavedItemType;
  itemId?: string;
};

const DEFAULT_PAGE_SIZE = 20;

function accountEndpoint(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const params = new URLSearchParams();
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    params.set(key, String(value));
  });
  const serialized = params.toString();
  return serialized ? `${path}?${serialized}` : path;
}

async function readApiJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || fallbackMessage);
  return payload;
}

async function fetchAccountList<T>(path: string, cursor?: string | null, limit = DEFAULT_PAGE_SIZE) {
  const response = await fetchWithAppAuth(accountEndpoint(path, { limit, cursor }), { method: 'GET' });
  const payload = await readApiJson<AccountListResponse<T>>(response, 'Unable to load account data.');
  return {
    items: payload.items ?? [],
    nextCursor: payload.nextCursor ?? null,
    limit: payload.limit ?? limit,
  };
}

async function fetchAccountSummary() {
  const response = await fetchWithAppAuth('/api/account/summary', { method: 'GET' });
  const payload = await readApiJson<AccountSummaryResponse>(response, 'Unable to load account summary.');
  if (!payload.stats) throw new Error('Account summary did not include stats.');
  return {
    stats: payload.stats,
    subscription: payload.subscription ?? null,
  };
}

function useAsyncList<T>(path: string | null, limit = DEFAULT_PAGE_SIZE) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const requestPage = useCallback(async (cursor: string | null, replace: boolean) => {
    if (!path) {
      setItems([]);
      setNextCursor(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    try {
      if (replace) setLoading(true);
      else setLoadingMore(true);
      const page = await fetchAccountList<T>(path, cursor, limit);
      setItems((current) => (replace ? page.items : [...current, ...page.items]));
      setNextCursor(page.nextCursor);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load account data.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [limit, path]);

  const refresh = useCallback(() => requestPage(null, true), [requestPage]);
  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return Promise.resolve();
    return requestPage(nextCursor, false);
  }, [loadingMore, nextCursor, requestPage]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { items, loading, loadingMore, error, nextCursor, refresh, loadMore };
}

export function useAccountSummary(userUid?: string | null) {
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userUid) {
      setSummary(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setSummary(await fetchAccountSummary());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load account summary.');
    } finally {
      setLoading(false);
    }
  }, [userUid]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { summary, loading, error, refresh };
}

export function useOrders(userUid?: string | null) {
  return useAsyncList<OrderDoc>(userUid ? '/api/account/orders' : null);
}

export function useLicenses(userUid?: string | null) {
  return useAsyncList<import('@/types/reader-personal').ReaderLibraryLicense>(userUid ? '/api/account/licenses' : null);
}

export function useDownloads(userUid?: string | null) {
  return useAsyncList<LicenseDoc>(userUid ? '/api/account/downloads' : null);
}

export function useNotifications(userUid?: string | null) {
  return useAsyncList<NotificationDoc>(userUid ? '/api/account/notifications' : null);
}

export function useSavedItems(userUid?: string | null) {
  return useAsyncList<AccountSavedItemDoc>(userUid ? '/api/account/saved' : null);
}

export function useSubscription(userUid?: string | null) {
  const [subscription, setSubscription] = useState<SubscriptionDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userUid) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const summary = await fetchAccountSummary();
      setSubscription(summary.subscription);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load subscription.');
    } finally {
      setLoading(false);
    }
  }, [userUid]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refresh();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [refresh]);

  return { subscription, loading, error, refresh };
}

export async function markNotificationRead(notificationId: string, read: boolean) {
  const response = await fetchWithAppAuth(`/api/account/notifications/${encodeURIComponent(notificationId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ read }),
  });
  const payload = await readApiJson<{ notification?: NotificationDoc }>(response, 'Unable to update notification.');
  return payload.notification ?? null;
}

export async function saveAccountSavedItem(input: SaveAccountItemInput) {
  const response = await fetchWithAppAuth('/api/account/saved', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readApiJson<{ saved?: AccountSavedItemDoc; created?: boolean }>(response, 'Unable to save item.');
}

export async function getAccountSavedStatus(input: Pick<SaveAccountItemInput, 'itemType' | 'itemId'>) {
  const response = await fetchWithAppAuth(accountEndpoint('/api/account/saved/status', input), {
    method: 'GET',
  });
  return readApiJson<{ saved?: AccountSavedItemDoc | null; exists?: boolean }>(response, 'Unable to load saved status.');
}

export async function deleteAccountSavedItem(input: DeleteAccountSavedItemInput) {
  const response = await fetchWithAppAuth('/api/account/saved', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readApiJson<{ deleted?: boolean }>(response, 'Unable to remove saved item.');
}

export async function startAiSubscriptionCheckout(plan: 'pro' | 'studio' | 'imprint') {
  const response = await fetchWithAppAuth('/api/payments/stripe/subscription-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan, successHref: '/account/subscription', cancelHref: '/account/subscription' }),
  });
  const payload = await readApiJson<{ url?: string }>(response, 'Unable to create AI subscription checkout.');
  if (!payload.url) throw new Error('Stripe checkout URL was not returned.');
  window.location.assign(payload.url);
}

export async function openAiBillingPortal() {
  const response = await fetchWithAppAuth('/api/payments/stripe/billing-portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ returnHref: '/account/subscription' }),
  });
  const payload = await readApiJson<{ url?: string }>(response, 'Unable to open billing portal.');
  if (!payload.url) throw new Error('Stripe billing portal URL was not returned.');
  window.location.assign(payload.url);
}

export async function openLicenseDownload(licenseId: string, digitalFormat?: 'epub' | 'pdf') {
  const query = digitalFormat ? `?format=${digitalFormat}` : '';
  const response = await fetchWithAppAuth(`/api/account/library/download/${encodeURIComponent(licenseId)}${query}`, {
    method: 'GET',
  });
  const payload = await readApiJson<{ url?: string }>(response, 'Unable to prepare download.');
  if (!payload.url) throw new Error('Download URL was not returned.');
  window.location.assign(payload.url);
}
